import { memo, useEffect, useState } from 'react'
import { IDurationProps } from '../../../../../Duration/Duration'
import { PATH_JOINER } from '../../../../../utils/getConfigBitPath'
import {
    selectRuleConfigBit,
    selectRuleIsChecked,
} from '../../../../../../../store/selectors/ruleCardSelectors'
import { useAppDispatch } from '../../../../../../../hooks/useAppDispatch'
import { useAppSelector } from '../../../../../../../hooks/useAppSelector'
import { inputValidationCheck } from '../../../../../../../validators/inputValidators/inputValidators'
import { useFormValidation } from '../../../../../../../hooks/useFormValidation'
import { getLabel } from '../../../../../../../utils/getLabel'
import { numberInputErrors } from '../../../../../../../utils/validationErrors/numberInputErrors'
import { initInputData } from '../../../../../../../utils/InitInputData'
import { inputDataDispatcher } from '../../../../../../../utils/InputDataDispatcher'
import {
    buildControlsExt,
    integerInput,
} from '../../../../../../inputs/controls'

export const RuleDuration = memo(function RuleDuration({
    dataPath,
    pathWithElementID,
    dataSchema,
    name,
    wrapper,
}: IDurationProps) {
    const dispatch = useAppDispatch()
    const pathSplit = dataPath.split(PATH_JOINER)
    const configBit = useAppSelector(selectRuleConfigBit(pathSplit))
    const path = [...dataPath.split(PATH_JOINER), name]
    const [checkPath, setCheckPath] = useState<string[]>([])
    const isChecked = useAppSelector(selectRuleIsChecked)
    const {
        is_required: isRequired,
        name: inputLabel,
        type,
        description: tooltipText,
        format,
        default: defaultValue,
        change_warning: changeWarning,
    } = dataSchema
    const validationCheck = inputValidationCheck(type, dataSchema)
    const [
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult,
        isValid,
    ] = useFormValidation(configBit, { [name]: validationCheck })

    const label: string = getLabel(name, inputLabel)
    const className: string = !wrapper ? 'w-50' : ''
    const errorKey: string = pathWithElementID
        ? [...pathWithElementID.split(PATH_JOINER), name].join(PATH_JOINER)
        : path.join(PATH_JOINER)
    const errMsg = numberInputErrors(inputState?.[name], dataSchema)
    const isDataEmpty: number = Object.keys(configBit || {}).length
    const isUndefined: boolean = typeof configBit?.[name] === 'undefined'
    const isEmpty: boolean = !configBit || !isDataEmpty || isUndefined

    useEffect(() => {
        if (dataPath.length) {
            setCheckPath(dataPath.split(PATH_JOINER))
            initInputData(
                {
                    isEmpty,
                    name,
                    path,
                    dataSchema,
                    isRequired,
                    errorKey,
                    data: configBit,
                    setInputState,
                    isRuleCard: true,
                },
                dispatch
            )
        }
    }, []) // eslint-disable-line

    useEffect(() => {
        if (isChecked && dataPath.length) {
            isValid()
        }
    }, [isChecked]) // eslint-disable-line

    useEffect(() => {
        if (configBit && dataPath.length) {
            setInputState(configBit)
        }
    }, [configBit]) // eslint-disable-line

    useEffect(() => {
        if (dataPath.length) {
            inputDataDispatcher(
                {
                    isEmpty,
                    name,
                    path,
                    dataSchema,
                    data: configBit,
                    inputState,
                    validationCheck,
                    errorKey,
                    isUndefined,
                    checkPath,
                    type,
                    isRuleCard: true,
                },
                dispatch
            )
        }
    }, [touched[name], isChecked]) // eslint-disable-line

    const handleOnBlur = () => {
        inputDataDispatcher(
            {
                isEmpty,
                name,
                path,
                dataSchema,
                data: configBit,
                inputState,
                validationCheck,
                errorKey,
                isUndefined,
                checkPath,
                type,
                isRuleCard: true,
            },
            dispatch
        )
        setTouched && setTouched({ ...touched, [name]: true })
        if (inputState[name] === '') {
            setInputState((prev: any) => ({ ...prev, [name]: null }))
        }
    }

    const controlDescriptors = [
        integerInput({
            name,
            validationType: 'integer',
            tooltipText,
            placeholder: defaultValue || `${format}...`,
            label,
            className,
            isColumn: true,
            errMsg,
            isRequired,
            changeWarning,
            onBlur: handleOnBlur,
        }),
    ]

    const DurationInput = buildControlsExt(
        controlDescriptors,
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult
    )

    return <div className={`${!wrapper ? 'w-100' : ''}`}>{DurationInput}</div>
})
